// @ts-strict-ignore
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { Dictionary } from 'typescript-collections';
import { ArticleManager } from '@article/manager/article.manager';
import { ProjectModel } from '@project/model/project.model';
import { BusbarArticleConnectProcessor } from '@project/components/article/processor/prepare/connect/busbar-article-connect.processor';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { NumberUtils } from '@shared/util/number-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { Line } from '@3d-scene/model/line';
import { Vector3 } from 'three';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleContainersWrapper } from '@3d-scene/model3d/article-containers-wrapper';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { ArticleContainerPropertyDef } from '@article/constants/article-container-property-def';
import { ConnectionAreaVO, MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ArticleCreationManager } from '@project/components/article/manager/article-creation.manager';
import { CloneUtils } from '@shared/util/clone-utils';
import { UndoRedoManager } from '@shared/undo/undo-redo.manager';
import { UndoDetachArticlesOperation } from '@shared/undo/operation/undo-detach-articles-operation';
import { UndoAttachArticlesOperation } from '@shared/undo/operation/undo-attach-articles-operation';
import { UndoMoveArticlesOperation } from '@shared/undo/operation/undo-move-articles-operation';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { UndoResizeBusbarOperation } from '@shared/undo/operation/undo-resize-busbar-operation';
import { ArticleLineHolder } from '@article/helper/article-line-holder';
import { CarrierBlockManager } from '../manager/carrier-block.manager';
import { LayerManager } from '@3d-scene/manager/layer-manager';
import { ProjectContentPresenter } from '@shared/presenter/project-content.presenter';
import { UnusedLabelingService } from '../../label/label-editor/service/unused-labeling.service';
import { ArticleMoveManager } from '../manager/article-move.manager';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { ArticleCreationManagerFactory } from '../manager/article-creation.manager.factory';
import { Log } from '@shared/debug/log';
import { ProjectArticleService } from '@project/service/project-article.service';
import { SelectionModelService } from '../service/selection-model.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ReplaceBusbarUtils } from '@article/helper/replace/busbar/replace-busbar-utils';
import { StringUtils } from '@shared/util/string-utils';

export class BusbarArticleWizardPresenter {
  public static readonly MM_DISPLAY_FRACTION_DIGITS: number = 1;
  public static readonly MM_DISPLAY_ROUNDING_THRESHOLD: number = 0.65;

  private _insertArticleHelper: InsertArticleHelper;
  private _existingBusbarContainer: ArticleContainer;
  private _busbarArticleUsage: ArticleUsageVO;
  private _busbarArticle: ArticleVO;

  private _wrapperGuidsToProcess = [];
  private _articleContainerWrappersMap: Dictionary<any, any> = new Dictionary<any, any>();
  private _articleWrapperGuidToDefaultConnection: Dictionary<any, any> = new Dictionary<any, any>();
  private _givingCubes = [];

  private _detachOperation: UndoDetachArticlesOperation = null;
  private _attachOperation: UndoAttachArticlesOperation = null;
  private _moveOperation: UndoMoveArticlesOperation = null;
  private _oldArticleGUID: string;

  public isArticlesConnected: boolean = false;
  public startOffsetLength: number = 0;
  public endOffsetLength: number = 0;
  public minStartLength: number = 0;
  public minEndLength: number = 0;
  public minStartLengthInMillimeters: number = 0;
  public minEndLengthInMillimeters: number = 0;
  public busbarLength: number = 0;
  public busbarMaxLength: number = 0;
  public distanceFromStartToFirstArticle: number = 0;
  public distanceFromStartToLastArticle: number = 0;

  public articleCreationManager: ArticleCreationManager;

  constructor(
    articleCreationManagerFactory: ArticleCreationManagerFactory,
    private articleLineHolder: ArticleLineHolder,
    private articleManager: ArticleManager,
    private selectionModelService: SelectionModelService,
    private projectModel: ProjectModel,
    private projectArticleService: ProjectArticleService,
    private projectMetaElementService: ProjectMetaElementService,
    private busbarArticleConnectProcessor: BusbarArticleConnectProcessor,
    private undoRedoManager: UndoRedoManager,
    private undoRedoUtils: UndoRedoUtils,
    private containerService: ContainerService,
    private carrierBlockManager: CarrierBlockManager,
    private articleMoveManager: ArticleMoveManager,
    private layerManager: LayerManager,
    private projectContentPresenter: ProjectContentPresenter,
    private unusedLabelingService: UnusedLabelingService,
    private insertArticleHelperFactory: InsertArticleHelperFactory,
    private insertArticleUtils: InsertArticleUtils,
    private applicationRefreshService: ApplicationRefreshService,
    private detachArticleUsageService: DetachArticleUsageService
  ) {
    this.articleCreationManager = articleCreationManagerFactory.getNewInstance()
  }

  private initBusbarData(container: ArticleContainer): void {
    this.startOffsetLength = ReplaceBusbarUtils.getBusbarStartOffset(container);
    this.endOffsetLength = ReplaceBusbarUtils.getBusbarEndOffset(container);
    this.busbarMaxLength = this.getBusbarMaxLength(container);
    this._insertArticleHelper = this.insertArticleHelperFactory.createInstanceByRangeOnLineArticleContainer(container);
    if (this._insertArticleHelper) {
      this.busbarLength = this._insertArticleHelper.baseLine.length - (this.startOffsetLength + this.endOffsetLength);
      const connectedArticles: ArticleContainer[] = this._insertArticleHelper.getAllConnectedArticles();
      const commonArticlesLine: AdvancedLine = this._insertArticleHelper.calculateCommonArticlesLine(connectedArticles);
      if (commonArticlesLine) {
        this.isArticlesConnected = true;
        this.distanceFromStartToFirstArticle = this._insertArticleHelper.getDistanceFromStart(commonArticlesLine.start);
        this.distanceFromStartToLastArticle = this._insertArticleHelper.getDistanceFromStart(commonArticlesLine.end);
        this.calculateMinStartEndLength();
      }
    }
  }

  private calculateMinStartEndLength(): void {
    const startOffsetLengthMM: number = this.getRoundedValueInMillimeters(this.startOffsetLength);
    if (this.distanceFromStartToFirstArticle < this.startOffsetLength) {
      const distanceFromStartToFirstArticleMM: number = this.getRoundedValueInMillimeters(this.distanceFromStartToFirstArticle);
      this.minStartLength = this.startOffsetLength - this.distanceFromStartToFirstArticle;
      this.minStartLengthInMillimeters = this.getRoundedValue(startOffsetLengthMM - distanceFromStartToFirstArticleMM);
    }
    if (this.distanceFromStartToLastArticle > this.startOffsetLength + this.busbarLength) {
      const busbarLengthMM: number = this.getRoundedValueInMillimeters(this.busbarLength);
      const distanceFromStartToLastArticleMM: number = this.getRoundedValueInMillimeters(this.distanceFromStartToLastArticle);
      this.minEndLength = this.distanceFromStartToLastArticle - (this.startOffsetLength + this.busbarLength);
      this.minEndLengthInMillimeters = this.getRoundedValue(distanceFromStartToLastArticleMM - (startOffsetLengthMM + busbarLengthMM));
    }
  }

  public getRoundedValueInMillimeters(value: number): number {
    return this.getRoundedValue(this.getValueInMillimeters(value));
  }

  public getValueInMillimeters(value: number): number {
    return UnitConverter.fromTo('3d', 'mm', value);
  }

  public getRoundedValue(value: number): number {
    return MathUtils.roundNumberValue(
      value,
      BusbarArticleWizardPresenter.MM_DISPLAY_FRACTION_DIGITS,
      BusbarArticleWizardPresenter.MM_DISPLAY_ROUNDING_THRESHOLD
    );
  }

  private getBusbarMaxLength(container: ArticleContainer): number {
    const result: number = ArticleUtils.getArticleBehaviorPropertyNumberValue(container.article, ArticleBehaviorPropertyDef.MAX_LENGTH);
    return isNaN(result) ? 0 : this.millimetersToAway3DUnits(result);
  }

  public millimetersToAway3DUnits(value: number, roundConvertedValue: boolean = true): number {
    if (NumberUtils.isNotNumber(value)) {
      return NaN;
    }
    return UnitConverter.fromToRounded('mm', '3d', value, roundConvertedValue ? 2 : -1);
  }

  public get busbarArticleUsage(): ArticleUsageVO {
    return this._busbarArticleUsage;
  }

  public set busbarArticleUsage(value: ArticleUsageVO) {
    this._busbarArticleUsage = value;
    if (value) {
      this._busbarArticle = this.articleManager.getCachedArticle(this._busbarArticleUsage.articleId, this._busbarArticleUsage.articleVersion);
      this._existingBusbarContainer = this.containerService.findArticleContainerInGlobal(this._busbarArticleUsage.id);
      this.initBusbarData(this._existingBusbarContainer);
    }
  }

  public async startLoadingBusbar(configuredStartLength: number, configuredEndLength: number): Promise<void> {
    const moveMap: Dictionary<string, Vector3> = new Dictionary<string, Vector3>();
    const baseLine: Line = this._insertArticleHelper.baseLine;
    if (configuredStartLength < this.startOffsetLength) {
      const startMoveOffset: number = this.startOffsetLength - configuredStartLength;
      const endPoint: Vector3 = MathUtils.getInterpolated(baseLine.start, baseLine.end, 1 - startMoveOffset / baseLine.length);
      const startMoveLine: AdvancedLine = new AdvancedLine(baseLine.start, endPoint);
      const startMoveMap: Dictionary<string, Vector3> = this._insertArticleHelper.moveArticlesToCreateFreeSpace(startMoveLine);
      InsertArticleUtils.mergeMoveMap(moveMap, startMoveMap);
    }
    if (configuredEndLength < this.endOffsetLength) {
      const endMoveOffset: number = this.endOffsetLength - configuredEndLength;
      const startPoint: Vector3 = MathUtils.getInterpolated(baseLine.end, baseLine.start, 1 - endMoveOffset / baseLine.length);
      const endMoveLine: AdvancedLine = new AdvancedLine(startPoint, baseLine.end);
      const endMoveMap: Dictionary<string, Vector3> = this._insertArticleHelper.moveArticlesToCreateFreeSpace(endMoveLine, null, true, false, true);
      InsertArticleUtils.mergeMoveMap(moveMap, endMoveMap);
    }
    if (!moveMap.isEmpty()) {
      this._moveOperation = new UndoMoveArticlesOperation(
        this.carrierBlockManager,
        this.articleMoveManager,
        this.layerManager,
        this.projectContentPresenter,
        this.unusedLabelingService,
        this.projectModel,
        this.insertArticleUtils,
        moveMap
      );
    }

    await this.performResize(this._existingBusbarContainer.articleGUID, configuredStartLength, configuredEndLength);
  }

  private async performResize(busbarGUID: string, additionalLengthStart: number, additionalLengthEnd: number): Promise<void> {
    this.selectionModelService.removeAllSelections();
    const connectedArticleUsages: ArticleUsageVO[] = [];
    const busbarContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(busbarGUID);
    const articleUsage: ArticleUsageVO = busbarContainer.articleUsage;
    let articleUsageToCreate: ArticleUsageVO = articleUsage ? CloneUtils.cloneArticleUsageVO(articleUsage) : null;
    const connectionCubes: Connection[] = this.getAcceptingCubesByBusbarArticleUsage(articleUsage);
    this._oldArticleGUID = articleUsage.id;
    this._givingCubes = this.getGivingCubesByBusbarArticleUsage(articleUsage);
    const article: ArticleVO = this.articleManager.getCachedArticle(articleUsageToCreate.articleId, articleUsageToCreate.articleVersion);
    if (!articleUsageToCreate) {
      articleUsageToCreate = this.projectArticleService.createUnstoredNewArticleUsage(article.identity, article.version);
    }
    articleUsageToCreate.metaElementUsages = [];
    const connectedUsageIds: string[] = ArticleUsageUtils.getAcceptingAreaIds(articleUsage);
    connectedUsageIds.forEach((usageId: string) => {
      connectedArticleUsages.push(this.projectModel.getArticleUsage(usageId));
      ArticleUsageUtils.removeAssociationsWithUsage(articleUsage, usageId);
    });
    this.articleLineHolder.clearArticleLineCacheByGUIDs(connectedUsageIds);
    ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
      articleUsageToCreate,
      ArticleUsageInfoPropertyDef.ARTICLE_ADDITIONAL_LENGTH_START,
      additionalLengthStart
    );
    ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
      articleUsageToCreate,
      ArticleUsageInfoPropertyDef.ARTICLE_ADDITIONAL_LENGTH_END,
      additionalLengthEnd
    );
    const usageIdToCreate: string = StringUtils.createUuid();
    ArticleUsageUtils.replaceArticleUsageId([articleUsageToCreate], busbarGUID, usageIdToCreate);
    ArticleUsageUtils.replaceArticleUsageId(connectedArticleUsages, busbarGUID, usageIdToCreate);
    this.projectArticleService.storeArticleUsage(articleUsageToCreate);
    this._detachOperation = this.undoRedoUtils.createUndoDetachOperationByArticleContainers([this._existingBusbarContainer]);
    if (articleUsage) {
      this.selectionModelService.removeAllSelections();
      await this.detachArticleUsage(articleUsage);
    }
    const wrappers: ArticleContainersWrapper[] = this.busbarArticleConnectProcessor.preProcessWithoutRefresh(article, connectionCubes);

    for (let wrapper of wrappers) {
      wrapper.articleUsageID = articleUsageToCreate.id;
      this._wrapperGuidsToProcess.push(wrapper.wrapperGUID);
      const rectangle: Rectangle3D = wrapper.properties.getValue(ArticleContainerPropertyDef.RECTANGLE);
      rectangle.point1.x += additionalLengthStart;
      rectangle.point4.x += additionalLengthStart;
      rectangle.point2.x += additionalLengthStart;
      rectangle.point3.x += additionalLengthStart;
      this._articleContainerWrappersMap.setValue(wrapper.wrapperGUID, wrapper);
      this._articleWrapperGuidToDefaultConnection.setValue(wrapper.wrapperGUID, ArticleUtils.findFirstConnectionArea(article.metaElements, ConnectionAreaVO.SIDE_GIVING));

      try {
        await wrapper.refreshWrapper();
        this.articleWrapperCreated(wrapper.wrapperGUID)
      } catch (error) {
        Log.error('Could not refresh article wrapper: ', error)
      }
    }
  }

  private getAcceptingCubesByBusbarArticleUsage(articleUsage: ArticleUsageVO): Connection[] {
    const acceptingCubes: Connection[] = [];

    ArticleUsageUtils.backwardConnectionAreaUsages(articleUsage).forEach(connectionAreaUsage => {
      if (this.isAreaIdsContainIdentity(connectionAreaUsage.connection.givingAreaIds, connectionAreaUsage.identity)) {
        const acceptingContainerId: string = connectionAreaUsage.connection.acceptingAreaId.articleUsageId;
        const acceptingContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(acceptingContainerId);
        const cube: Connection = acceptingContainer.findFirstConnectionByName(connectionAreaUsage.connection.acceptingAreaId.metaElementName);
        if (cube) {
          acceptingCubes.push(cube);
        }
      }
    });
    return acceptingCubes;
  }

  private isAreaIdsContainIdentity(givingAreaIds: MetaElementUsageIdentityVO[], identity: MetaElementUsageIdentityVO): Boolean {
    if (!identity.articleUsageId) {
      return false;
    }
    let result: boolean = true;
    givingAreaIds.forEach((givingAreaIdentity: MetaElementUsageIdentityVO) => {
      if (identity.articleUsageId === givingAreaIdentity.articleUsageId) {
        result = true;
      }
    });
    return result;
  }

  private getGivingCubesByBusbarArticleUsage(articleUsage: ArticleUsageVO): Connection[] {
    const givingCubes: Connection[] = [];
    ArticleUsageUtils.forwardConnections(articleUsage).forEach(connection => {
      connection.givingAreaIds.forEach((givingIdentity: MetaElementUsageIdentityVO) => {
        const container: ArticleContainer = this.containerService.findArticleContainerInGlobal(givingIdentity.articleUsageId);
        const cube: Connection = container.findFirstConnectionByName(givingIdentity.metaElementName);
        if (cube) {
          givingCubes.push(cube);
        }
      });
    });
    return givingCubes;
  }

  private async detachArticleUsage(articleUsage: ArticleUsageVO): Promise<void> {
    const detachedArticle = await this.detachArticleUsageService.detachArticleUsageFromProjectById(articleUsage.id);
    if (detachedArticle) {
      this.applicationRefreshService.notifyProjectChange();
    }
  }

  public articleWrapperCreated(wrapperGuid: string): void {
    if (this._wrapperGuidsToProcess.indexOf(wrapperGuid) === -1) {
      return;
    }
    this._wrapperGuidsToProcess.splice(this._wrapperGuidsToProcess.indexOf(wrapperGuid), 1);
    const articleWrapper: ArticleContainersWrapper = this._articleContainerWrappersMap.getValue(wrapperGuid);
    const givingArea: ConnectionAreaVO = this._articleWrapperGuidToDefaultConnection.getValue(wrapperGuid);
    if (articleWrapper && givingArea) {
      this.busbarArticleConnectProcessor.process(articleWrapper.articleContainers, articleWrapper.baseArticleContainer, [givingArea], null);
    }
    const connection: ConnectionAreaVO = ConnectionInfoUtils.findDefaultRangeOnLineConnection(this._busbarArticle.metaElements);
    const acceptingCube: Connection = articleWrapper.baseArticleContainer.findFirstConnectionByName(connection.name);
    this._givingCubes.forEach((cube: Connection) => {
      this.projectMetaElementService.createUsageBetweenConnections(cube, acceptingCube);
    });
    if (this._wrapperGuidsToProcess.length === 0) {
      this._attachOperation = this.undoRedoUtils.createUndoAttachOperationByArticleContainers(articleWrapper.articleContainers);
      this.undoRedoManager.pushUndo(
        new UndoResizeBusbarOperation(
          this._attachOperation,
          this._detachOperation,
          this._oldArticleGUID,
          acceptingCube.articleContainer.articleGUID,
          this.containerService,
          this._moveOperation
        )
      );
      this._givingCubes = [];
    }
  }
}
